import { parseJwtCliengo } from './jwt-parser';

/**
 * Resultado sincrónico de clasificar el JWT (sin tocar la red):
 * - `no-jwt`: ausente, malformado o sin shape Cliengo.
 * - `ok`: `superadmin` → acceso inmediato, sin resolver el email.
 * - `needs-internal-check`: JWT válido sin `superadmin` → el acceso depende de
 *   si el caller es interno (email `@cliengo`/`@leadaki`), que NO está en el
 *   JWT y se resuelve aparte vía `GET /users/me` (ver `resolve-auth-status`).
 *
 * Nota: el privilegio `admin` por sí solo NO da acceso — es el privilegio de
 * admin de cuenta (lo tiene cualquier cliente sobre su propia cuenta).
 */
export type TokenCheck =
  | { kind: 'no-jwt' }
  | { kind: 'ok'; jwt: string; isAdmin: boolean; isSuperAdmin: boolean }
  | {
      kind: 'needs-internal-check';
      jwt: string;
      isAdmin: boolean;
      isSuperAdmin: boolean;
    };

/**
 * Clasifica un JWT. Función pura sin efectos — la decisión de render y la
 * resolución del email interno viven en `resolve-auth-status` / el guard.
 *
 * @param jwt El JWT crudo de la cookie. Vacío / undefined → `no-jwt`.
 */
export function verifyAuthToken(jwt: string | undefined): TokenCheck {
  if (!jwt) {
    return { kind: 'no-jwt' };
  }

  let payload;

  try {
    payload = parseJwtCliengo(jwt);
  } catch {
    return { kind: 'no-jwt' };
  }

  if (!payload) {
    return { kind: 'no-jwt' };
  }

  const isAdmin = payload.privileges.includes('admin');
  const isSuperAdmin = payload.privileges.includes('superadmin');

  if (isSuperAdmin) {
    return { kind: 'ok', jwt, isAdmin, isSuperAdmin };
  }

  return { kind: 'needs-internal-check', jwt, isAdmin, isSuperAdmin };
}
